import getUsageTracker from 'pricing-pages-lib/utils/getUsageTracker';
import { createToObject } from 'pricing-pages-lib/utils/queryParamUtils';
import { useIsMobile } from 'pricing-pages-ui-components/hooks/useMedia';
import ErrorPage from 'pricing-pages-ui/pages/ErrorPage';
import { Redirect, Route, Switch, useHistory } from 'react-router-dom';
import { FREE } from 'self-service-api/constants/ProductLevels';
import { CMS, MARKETING, OPERATIONS, SALES, SERVICE } from 'self-service-api/constants/ProductLines';
import MobileRoutes from './MobileRoutes';
import LoadingScreen from './components/LoadingScreen';
import { makeLoadable } from './components/common/LoadableWrapper';
import { ENTERPRISE, tabIdToProductLevels } from './constants/routing/Tabs';
import { useGlobalLoading } from './hooks/loadingHooks';
import { useCommerceHub } from './hooks/useCommerceHub';
import { useCompanyDetails } from './hooks/useCompanyDetails';
import { useRecommendedRouting } from './hooks/useRecommendations';
import { useShouldHideOpsOrSuitePage } from './hooks/useShouldHideOpsOrSuitePage';
import AppLayout from './layouts/AppLayout';
import AddOnsPage from './pages/AddOnsPageV1';
import BreezeIntelligencePage from './pages/BreezeIntelligencePage';
import BundlePage from './pages/BundlePage';
import CommerceHubPage from './pages/CommerceHubPage';
import CommercePage from './pages/CommercePage';
import FreeToolsPage from './pages/FreeToolsPageV1';
import HubSpotCreditsPage from './pages/HubSpotCreditsPage';
import HubSpotCreditsPageInbound25 from './pages/HubSpotCreditsPageInbound25';
import MarketingPlusPage from './pages/MarketingPlusPage';
import ProductPage from './pages/ProductPage';
import RecommendedPage from './pages/RecommendedPage';
import SmartCrmPage from './pages/SmartCrmPage';
import SuitePage from './pages/SuitePage';
import { useAppSelector } from './reducers';
import { isAppAuthSelector } from './selectors/AuthSelectors';
import { isUngatedForInbound25Selector, isUngatedForRecommendedViewSelector, isUngatedForSeatsSelector } from './selectors/GatingSelectors';
import { crmSuiteOwnershipLevel } from './selectors/ProductInventorySelectors';
import { PricingPageContainer } from './v2/PricingPageContainer';
import { useIsFreePortal } from './v2/hooks/useProductInventory';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const createAppAuthComponent = (Component, productLine) => {
  const AppAuthComponent = () => /*#__PURE__*/_jsx(Component, {
    productLine: productLine
  });
  return AppAuthComponent;
};
const createTabComponent = (Component, productLine, path) => {
  const TabComponent = ({
    match: {
      params: {
        tab
      }
    }
  }) => {
    if (!tab || !Object.keys(tabIdToProductLevels).includes(tab)) {
      getUsageTracker().setProperties({
        defaultedTab: ENTERPRISE
      });
      return /*#__PURE__*/_jsx(Redirect, {
        to: createToObject(`${path}/${ENTERPRISE}`)
      });
    }
    return /*#__PURE__*/_jsx(Component, {
      productLine: productLine
    });
  };
  return TabComponent;
};
const ProductPageRoute = ({
  path,
  Component = ProductPage,
  productLine
}) => {
  const isAppAuth = useAppSelector(isAppAuthSelector);
  const shouldHideOpsOrSuitePage = useShouldHideOpsOrSuitePage();
  if (shouldHideOpsOrSuitePage && (path === '/operations' || path === '/data' || path === '/suite')) {
    return /*#__PURE__*/_jsx(Redirect, {
      from: path,
      exact: true,
      to: createToObject('/marketing')
    });
  }
  if (isAppAuth) {
    const AppAuthComponent = createAppAuthComponent(Component, productLine);
    return /*#__PURE__*/_jsx(Route, {
      path: path,
      component: AppAuthComponent
    });
  }
  const TabComponent = createTabComponent(Component, productLine, path);
  return /*#__PURE__*/_jsx(Route, {
    path: `${path}/:tab?`,
    component: TabComponent
  });
};
const InterstitialPage = makeLoadable({
  loader: () => import( /* webpackChunkName: "interstitial-page" */'pricing-pages-ui/components/interstitial/InterstitialPage')
});
const DefaultInAppRoute = () => {
  const {
    recommendedRoute,
    loading: isLoadingRecommendedRoute
  } = useRecommendedRouting();
  const isAppReady = useGlobalLoading();
  const {
    loading: isCompanyDetailsLoading,
    isVSBPortal
  } = useCompanyDetails();
  const isUngatedForSeats = useAppSelector(isUngatedForSeatsSelector);
  const ownedSuiteProductLevel = useAppSelector(crmSuiteOwnershipLevel);
  const {
    isFreePortal,
    loading: isInventoryLoading
  } = useIsFreePortal();
  const isFreeVSBPortal = isVSBPortal && isFreePortal;
  const ownsSuiteProduct = isUngatedForSeats && ownedSuiteProductLevel && ownedSuiteProductLevel !== FREE;
  if (!isAppReady || isLoadingRecommendedRoute || isCompanyDetailsLoading || isInventoryLoading) {
    return /*#__PURE__*/_jsx(LoadingScreen, {});
  }
  if (isFreeVSBPortal) {
    return /*#__PURE__*/_jsx(Redirect, {
      from: "/",
      exact: true,
      to: createToObject('/crm')
    });
  }
  if (ownsSuiteProduct) {
    return /*#__PURE__*/_jsx(Redirect, {
      from: "/",
      exact: true,
      to: createToObject('/suite')
    });
  }
  return /*#__PURE__*/_jsx(Redirect, {
    from: "/",
    exact: true,
    to: createToObject(`/${recommendedRoute}`)
  });
};

// *************** Important when adding new routes: ***************
// This component is shared by both the public and authed app.
// The public app enables nested routes within a hub to provide a view of each
// product by tier.
// The authed app disables nested routes within a product, with a check and
// redirect to the following regex path: path="/:page/*"
// When adding a new nested route to the auth app, add a check in the same line
// as the regex redirect to override this behavior

export default function Routes() {
  const isAppAuth = useAppSelector(isAppAuthSelector);
  const isUngatedForSeats = useAppSelector(isUngatedForSeatsSelector);
  const {
    loading: commerceHubLoading,
    showCommerceHubProductExperience,
    showCommerceHubPaymentsExperience
  } = useCommerceHub();
  getUsageTracker().setProperties({
    isSeatsPilot: isUngatedForSeats
  });
  const isMobile = useIsMobile();
  const history = useHistory();
  const isUngatedForRecommendedView = useAppSelector(isUngatedForRecommendedViewSelector);
  const isUngatedForInbound = useAppSelector(isUngatedForInbound25Selector);
  if (isMobile && !isAppAuth) {
    return /*#__PURE__*/_jsx(MobileRoutes, {});
  }
  return /*#__PURE__*/_jsx(AppLayout, {
    children: /*#__PURE__*/_jsxs(Switch, {
      children: [isAppAuth && !history.location.pathname.startsWith('/internal-development') && /*#__PURE__*/_jsx(Route, {
        path: "/:page/*",
        render: ({
          location,
          match: {
            params: {
              page
            }
          }
        }) => {
          return /*#__PURE__*/_jsx(Redirect, {
            to: Object.assign({}, location, {
              pathname: `/${page}`
            })
          });
        }
      }), /*#__PURE__*/_jsx(Route, {
        path: "/interstitial",
        render: () => {
          return /*#__PURE__*/_jsx(InterstitialPage, {});
        }
      }), /*#__PURE__*/_jsx(Route, {
        path: "/bundle",
        component: BundlePage
      }), /*#__PURE__*/_jsx(Route, {
        path: "/marketing-plus",
        component: MarketingPlusPage
      }), /*#__PURE__*/_jsx(ProductPageRoute, {
        path: "/suite",
        Component: SuitePage
      }), /*#__PURE__*/_jsx(Route, {
        path: "/splash/:tab?",
        render: ({
          location,
          match: {
            params: {
              tab
            }
          }
        }) => {
          return /*#__PURE__*/_jsx(Redirect, {
            to: Object.assign({}, location, {
              pathname: tab ? `/suite/${tab}` : '/suite'
            })
          });
        }
      }), /*#__PURE__*/_jsx(ProductPageRoute, {
        path: "/sales",
        productLine: SALES
      }), /*#__PURE__*/_jsx(ProductPageRoute, {
        path: "/service",
        productLine: SERVICE
      }), /*#__PURE__*/_jsx(ProductPageRoute, {
        path: "/content",
        productLine: CMS
      }), /*#__PURE__*/_jsx(Route, {
        path: "/commerce",
        render: () => {
          if (commerceHubLoading) {
            return /*#__PURE__*/_jsx(LoadingScreen, {});
          }

          // if neither experience is enabled, redirect to suite
          if (!showCommerceHubPaymentsExperience && !showCommerceHubProductExperience) {
            return /*#__PURE__*/_jsx(Redirect, {
              to: createToObject('/suite')
            });
          }
          return showCommerceHubProductExperience ? /*#__PURE__*/_jsx(CommerceHubPage, {}) : /*#__PURE__*/_jsx(CommercePage, {});
        }
      }), /*#__PURE__*/_jsx(Route, {
        path: "/cms/:tab?",
        render: ({
          location,
          match: {
            params: {
              tab
            }
          }
        }) => {
          return /*#__PURE__*/_jsx(Redirect, {
            to: Object.assign({}, location, {
              pathname: tab ? `/content/${tab}` : '/content'
            })
          });
        }
      }), /*#__PURE__*/_jsx(ProductPageRoute, {
        path: "/marketing",
        productLine: MARKETING
      }), isUngatedForInbound ? /*#__PURE__*/_jsx(ProductPageRoute, {
        path: "/data",
        productLine: OPERATIONS
      }) : /*#__PURE__*/_jsx(ProductPageRoute, {
        path: "/operations",
        productLine: OPERATIONS
      }), isUngatedForInbound ? /*#__PURE__*/_jsx(Route, {
        path: "/operations/:tab?",
        render: ({
          location,
          match: {
            params: {
              tab
            }
          }
        }) => {
          return /*#__PURE__*/_jsx(Redirect, {
            to: Object.assign({}, location, {
              pathname: tab ? `/data/${tab}` : '/data'
            })
          });
        }
      }) : null, /*#__PURE__*/_jsx(ProductPageRoute, {
        path: "/smart-crm",
        Component: SmartCrmPage
      }), /*#__PURE__*/_jsx(Route, {
        path: "/crm",
        component: FreeToolsPage
      }), /*#__PURE__*/_jsx(Route, {
        path: "/add-ons",
        component: AddOnsPage
      }), /*#__PURE__*/_jsx(Route, {
        exact: true,
        path: "/breeze-intelligence",
        component: BreezeIntelligencePage
      }), /*#__PURE__*/_jsx(Route, {
        path: "/breeze-intelligence/*",
        render: ({
          location
        }) => {
          return /*#__PURE__*/_jsx(Redirect, {
            to: Object.assign({}, location, {
              pathname: '/breeze-intelligence'
            })
          });
        }
      }), /*#__PURE__*/_jsx(Route, {
        exact: true,
        path: "/hubspot-credits",
        component: isUngatedForInbound ? HubSpotCreditsPageInbound25 : HubSpotCreditsPage
      }), /*#__PURE__*/_jsx(Route, {
        path: "/hubspot-credits/*",
        render: ({
          location
        }) => {
          return /*#__PURE__*/_jsx(Redirect, {
            to: Object.assign({}, location, {
              pathname: '/hubspot-credits'
            })
          });
        }
      }), /*#__PURE__*/_jsx(Route, {
        path: "/recommended",
        component: isUngatedForRecommendedView ? RecommendedPage : () => {
          return /*#__PURE__*/_jsx(Redirect, {
            to: "/"
          });
        }
      }), /*#__PURE__*/_jsx(Route, {
        path: "/internal-development",
        component: PricingPageContainer
      }), isAppAuth ? /*#__PURE__*/_jsx(DefaultInAppRoute, {}) : /*#__PURE__*/_jsx(Redirect, {
        from: "/",
        exact: true,
        to: createToObject('/suite')
      }), /*#__PURE__*/_jsx(Route, {
        component: ErrorPage
      })]
    })
  });
}